import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, CreditCard, Info, XCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { brl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

function CancelButton() {
  const { planId, trialActive, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  if (planId === "gratis" || trialActive) return null;
  const cancel = async () => {
    if (!window.confirm("Tem certeza? Sua assinatura será cancelada no Mercado Pago e sua conta voltará ao plano Grátis.")) return;
    setLoading(true);
    try {
      await api.post("/payments/subscription/cancel");
      toast.success("Assinatura cancelada. Sua conta voltou ao plano Grátis.");
      refresh();
    } catch (e) {
      toast.error(apiError(e, "Não foi possível cancelar. Tente novamente ou fale com o suporte."));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="text-center">
      <button onClick={cancel} disabled={loading}
              className="text-sm text-red-600 hover:underline inline-flex items-center gap-1"
              data-testid="cancel-subscription-btn">
        <XCircle className="h-4 w-4" /> {loading ? "Cancelando..." : "Cancelar minha assinatura"}
      </button>
    </div>
  );
}

export default function Plans() {
  const { planId, trialActive, trialEndsAt, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);
  const [loading, setLoading] = useState("");

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    refresh();
  }, []);

  const subscribe = async (plan) => {
    const key = yearly ? plan.lookup_yearly : plan.lookup_monthly;
    if (!key) return;
    setLoading(plan.id || plan._id || plan.name);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e, "Não foi possível iniciar o pagamento."));
      setLoading("");
    }
  };

  return (
    <div className="space-y-6 animate-in-up max-w-4xl mx-auto" data-testid="plans-page">
      <div className="text-center">
        <h1 className="text-2xl font-bold tracking-tight">Planos e assinatura</h1>
        <p className="text-sm text-slate-500 mt-1">
          {trialActive
            ? `Você está no teste do plano Profissional até ${new Date(trialEndsAt).toLocaleDateString("pt-BR")}.`
            : `Seu plano atual: ${plans.find((p) => p.id === planId || p._id === planId)?.name || planId}`}
        </p>
        <div className="mt-4 inline-flex items-center bg-white border border-slate-200 rounded-full p-1">
          <button className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${!yearly ? "bg-slate-900 text-white" : "text-slate-600"}`}
                  onClick={() => setYearly(false)} data-testid="billing-monthly">Mensal</button>
          <button className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${yearly ? "bg-slate-900 text-white" : "text-slate-600"}`}
                  onClick={() => setYearly(true)} data-testid="billing-yearly">
            Anual <span className="text-green-500 text-xs">2 meses grátis</span>
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((p) => {
          const id = p.id || p._id;
          const current = id === planId && !trialActive;
          const price = yearly ? p.price_yearly : p.price_monthly;
          return (
            <Card key={id} className={`shadow-sm ${p.recommended ? "border-sky-500 border-2" : "border-slate-200"}`} data-testid={`plan-card-${id}`}>
              <CardContent className="p-6">
                {p.recommended && <Badge className="bg-sky-600 text-white mb-2">Recomendado</Badge>}
                <h2 className="font-display font-bold text-xl">{p.name}</h2>
                <p className="mt-2 font-mono-num text-3xl font-semibold">
                  {price === 0 ? "R$ 0" : brl(price)}
                  <span className="text-sm font-normal text-slate-500">{price === 0 ? "" : yearly ? "/ano" : "/mês"}</span>
                </p>
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                <Button
                  className={`w-full h-12 mt-5 ${p.recommended ? "bg-sky-600 hover:bg-sky-700 text-white" : ""}`}
                  variant={p.recommended ? "default" : "outline"}
                  disabled={current || price === 0 || loading === id}
                  onClick={() => subscribe(p)}
                  data-testid={`subscribe-${id}-btn`}
                >
                  {current ? "Plano atual" : price === 0 ? "Plano gratuito" : loading === id ? "Abrindo checkout..." : "Assinar"}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-5 flex gap-3 text-sm text-slate-600">
          <CreditCard className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-slate-800">Pagamento processado pelo Mercado Pago</p>
            <p className="mt-1">
              Pague com <b>Pix</b>, <b>cartão de crédito</b> ou <b>boleto</b>. A renovação da assinatura é
              automática e você pode cancelar quando quiser — sua conta volta ao plano Grátis sem perder dados.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Importante: no checkout do Mercado Pago, entre com a conta que usa o <b>mesmo e-mail do seu cadastro GEPR</b> — o Mercado Pago exige que o e-mail da conta de pagamento seja igual ao da assinatura.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-5 flex gap-3 text-sm text-amber-900">
          <Info className="h-5 w-5 shrink-0 mt-0.5" />
          <p>
            Sem fidelidade: cancele quando quiser aqui mesmo ou pelo painel do Mercado Pago. Ao cancelar,
            sua conta volta ao plano Grátis e seus dados continuam com você.
          </p>
        </CardContent>
      </Card>
      <CancelButton />
    </div>
  );
}
